// Story 11.11 — the country VAT register's read, and its two audited,
// forward-only write actions.
//
// ⛔ THERE IS NO PATCH AND NO DELETE HERE, AND THERE NEVER WILL BE.
// `country_vat_rules` is append-only at the database level (migration 0084's
// trigger trio), because every past top-up was stamped against the rule in force
// at its own payment instant: editing a row would silently re-answer a question
// already answered. A wrong rule is superseded by a new rule with a LATER
// effective date, and `note` is where that supersession says why.
//
// ⚠ AND UNLIKE STORY 11.3's REGISTRATION STATE, THIS TABLE CANNOT SUPERSEDE ON
// THE SAME DAY. Its primary key is `(country_code, effective_from)` and its
// in-force read has no `recorded_at` tie-break, so re-recording today's date
// comes back 409 rather than correcting the row. The server says so in the
// conflict message; the form's helper text says so before you get there.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

/** The four `country_vat_rules.status` values the panel can write. */
export type RuleStatus =
  | "eu_member_state"
  | "researched_rate"
  | "researched_zero"
  | "researched_special"
  | "hard_blocked";

export type ReportingRail = "eu_oss" | "domestic" | "own_rail" | "special";

/** ⚑ Derived server-side from the register row plus the registration phase. */
export type ProvisionPosture =
  | "provisioned_at_setting"
  | "own_rail_hedge"
  | "own_rail_rate_missing"
  | "registered_booked"
  | "researched_no_provision";

export type RegisterRuleView = {
  effective_from: string;
  status: string;
  /** `null` for a status that carries no national rate — ⛔ never a zero. */
  rate_bps: number | null;
  reporting_rail: string;
  regime: string;
  research_artifact: string;
  /** The typed source. `null` on a loader row, whose sources are in its file. */
  source_url: string | null;
  source_checked_on: string | null;
  loaded_at: string;
  recorded_by: string | null;
  audit_log_id: string | null;
  manual: boolean;
  /** `null` when the row carries no checked-on date — not the same as "old". */
  staleness_days: number | null;
  stale: boolean;
  in_force: boolean;
};

export type JurisdictionView = {
  country: string;
  status: string | null;
  rate_bps: number | null;
  reporting_rail: string | null;
  regime: string | null;
  effective_from: string | null;
  source_checked_on: string | null;
  staleness_days: number | null;
  stale: boolean;
  posture: ProvisionPosture;
  future_rule_count: number;
  rules: RegisterRuleView[];
};

export type WithheldCountry = {
  country: string;
  research_artifact: string;
  /** Set only on `research_owes_an_update` — the rule the register applies. */
  register_status: string | null;
  register_rate_bps: number | null;
  register_effective_from: string | null;
};

export type VatRegisterOverview = {
  as_of: string;
  staleness_threshold_days: number;
  max_plausible_rate_bps: number;
  registration_state: "not_registered" | "registered";
  provision_rate_bps: number;
  /** ⚑ The link to Story 11.9's exposure surface. The euros live there. */
  exposure_surface_path: string;
  jurisdictions: JurisdictionView[];
  /**
   * Researched files that establish no rate AND have no rule in force in the
   * register: these really do resolve as unresearched today.
   */
  withheld: WithheldCountry[];
  /**
   * The other half, and the one that only exists because of this page: the file
   * still establishes no rate, but a rule IS in force because somebody typed it
   * in here. The register governs the money; the country file owes an update.
   */
  research_owes_an_update: WithheldCountry[];
  withheld_unavailable: string | null;
  jurisdiction_count: number;
  rule_count: number;
  manual_rule_count: number;
  stale_jurisdiction_count: number;
};

export type GetVatRegisterResponse = { data: VatRegisterOverview };

async function fetchVatRegister(): Promise<GetVatRegisterResponse> {
  const response = await fetch("/api/v1/admin/vat-register", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `VAT register fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as GetVatRegisterResponse;
}

export function useVatRegister() {
  return useQuery<GetVatRegisterResponse, Error>({
    queryKey: ["admin-vat-register"],
    queryFn: fetchVatRegister,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: false,
  });
}

export type RecordRuleInput = {
  country: string;
  status: RuleStatus;
  /** ⛔ Omitted entirely on a status row; required on a rate row. */
  rate_bps: number | null;
  reporting_rail: ReportingRail;
  effective_from: string;
  source_url: string;
  source_checked_on: string;
  note: string | null;
};

export type RecordRuleResponse = {
  data: {
    country: string;
    effective_from: string;
    status: string;
    rate_bps: number | null;
    reporting_rail: string;
    regime: string;
    research_artifact: string;
    source_url: string;
    source_checked_on: string;
    audit_log_id: string;
    loaded_at: string;
    in_force_now: boolean;
  };
};

export type RecordRuleError = {
  code: string;
  message: string;
  field?: string;
  status: number;
};

async function recordRule(
  path: string,
  input: RecordRuleInput
): Promise<RecordRuleResponse> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      // Fresh key per submit, matching the registration-toggle precedent: the
      // endpoint is covered by the idempotency middleware, so a network retry
      // replays the cached response instead of coming back 409 on a row it
      // wrote itself.
      "X-Idempotency-Key": crypto.randomUUID(),
    },
    body: JSON.stringify(input),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RecordRuleError = {
      code: "ADMIN_VAT_REGISTER_WRITE_FAILED",
      message: `Recording the rule failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as RecordRuleResponse;
}

function useRecordRule(path: string) {
  const queryClient = useQueryClient();
  return useMutation<RecordRuleResponse, RecordRuleError, RecordRuleInput>({
    mutationFn: (input) => recordRule(path, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-vat-register"] });
      // A new rule can move a country's exposure posture, and that panel is on
      // the metrics dashboard.
      queryClient.invalidateQueries({ queryKey: ["admin-metrics"] });
    },
  });
}

/** Add a RATE row — the Belgium/Malta path. */
export function useRecordVatRateRow() {
  return useRecordRule("/api/v1/admin/vat-register/rate-rows");
}

/** Add a rate-less STATUS row (`researched_special`, `hard_blocked`). */
export function useRecordVatStatusRow() {
  return useRecordRule("/api/v1/admin/vat-register/status-rows");
}
